<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<script lang="ts" setup>
import { nextTick, ref, watch } from 'vue';

const props = defineProps<{
  visible: boolean;
  annotationInputPosition: { x: number; y: number };
  existingText: string;
}>();

const emits = defineEmits<{
  (e: 'commit', text: string): void;
  (e: 'cancel'): void;
}>();
const annotationInputRef = ref<HTMLInputElement | null>(null);
const annotationInputValue = ref(props.existingText || '');

function commitAnnotationInput() {
  emits('commit', annotationInputValue.value);
}

function cancelAnnotationInput() {
  emits('cancel');
}

function focusInput() {
  nextTick(() => {
    annotationInputRef.value?.focus();
    annotationInputRef.value?.select();
  });
}

watch(
  () => props.visible,
  (newVal) => {
    if (newVal) {
      annotationInputValue.value = props.existingText || '';
      // Wait 10 ms to ensure the input is rendered
      setTimeout(() => {
        focusInput();
      }, 10);
    }
  },
);

watch(
  () => props.annotationInputPosition,
  () => {
    if (props.visible) {
      annotationInputValue.value = props.existingText || '';
      focusInput();
    }
  },
  { deep: true },
);

defineExpose({
  commitAnnotationInput,
});
</script>

<template>
  <input
    ref="annotationInputRef"
    v-model="annotationInputValue"
    class="annotation-input"
    type="text"
    :style="{
      left: `${annotationInputPosition.x}px`,
      top: `${annotationInputPosition.y}px`,
    }"
    placeholder="Enter text"
    @keydown.enter.prevent="commitAnnotationInput()"
    @keydown.esc.prevent="cancelAnnotationInput"
    @keydown.space.stop
    @keydown.stop
    @blur="commitAnnotationInput"
    @mousedown.stop.prevent
  >
</template>

<style scoped>
.annotation-input {
  cursor: pointer;
  position: absolute;
  z-index: 1000;
  user-select: none;
  pointer-events: auto;
  padding: 4px 8px;
  border: 1px solid var(--md-primary);
  border-radius: 4px;
  background: var(--glass-bg-primary);
  color: var(--md-text-primary);
  max-width: 140px;
  transform: translate(-50%, -50%);
  text-align: center;
}
</style>
